import {Action, Attributes, AttributesRow, Card, DataStatus, Flag} from 'platform/components';
import {Box, Link, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {equals, isNil, isNotNil, not} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchListQuery, useGetCurrenciesQuery} from '@dms/api/core';
import {useGetPaymentTypeEnumQuery} from '@dms/api/finance';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {TenantResponseBody} from '@dms/api/shared';
import {
  GetReceiveNoteResponse,
  GetWarehouseResponse,
  useGetDeliveryNoteQuery,
  useGetSuppliersQuery,
  useGetWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';
import {getSupplierById, getWarehouseById} from '@dms/warehouse/shared';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {BasicInformationForm} from './BasicInformationForm';
import {BasicInformationReadOnly} from './BasicInformationReadOnly';
import {BranchBasedInformationForm} from './BranchBasedInformationForm';
import {BranchBasedInformationReadOnly} from './BranchBasedInformationReadOnly';

interface GeneralInformationTabProps extends RequiredTestIdProps {
  receiveNoteId: string;
  receiveNote: GetReceiveNoteResponse | Nullish;
  tenant: TenantResponseBody | Nullish;
  warehouse: GetWarehouseResponse | Nullish;
  isDraftReceiveNote: boolean;
  isFromReceiveNote: boolean;
}

export const GeneralInformationTab = (props: GeneralInformationTabProps) => {
  const dateTimeFormatter = useDateTimeFormatter();

  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const [isReadOnly, setIsReadOnly] = useState(isNotNilOrEmpty(props.receiveNoteId));

  const isBranchBased =
    props.receiveNote?.type === 'TRANSFER' ||
    props.receiveNote?.type === 'EXTERNAL_TRANSFER' ||
    props.receiveNote?.type === 'OPENING_BALANCE';

  const {data: authorizationProfiles, isLoading: isAuthorizationProfilesLoading} =
    useGetAuthorizationProfilesQuery(
      {'x-tenant': props.tenant?.id ?? ''},
      {skip: isNil(props.tenant)}
    );

  const {
    data: suppliers,
    isLoading: isSuppliersLoading,
    isError: isSuppliersError,
  } = useGetSuppliersQuery();

  const {
    data: branchList,
    isLoading: isBranchListLoading,
    isError: hasBranchListError,
  } = useGetBranchListQuery();

  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery();

  const {
    data: paymentTypes,
    isLoading: isPaymentTypesLoading,
    isError: isPaymentTypesError,
  } = useGetPaymentTypeEnumQuery();

  const {
    data: currencies,
    isLoading: isCurrenciesLoading,
    isError: isCurrenciesError,
  } = useGetCurrenciesQuery();

  const {
    data: deliveryNote,
    isLoading: isDeliveryNoteLoading,
    isError: isDeliveryNoteError,
  } = useGetDeliveryNoteQuery(
    {deliveryNoteId: props.receiveNote?.deliveryNote.deliveryNoteId as string},
    {skip: isNilOrEmpty(props.receiveNote)}
  );

  const isLoading =
    isAuthorizationProfilesLoading ||
    isSuppliersLoading ||
    isBranchListLoading ||
    isWarehousesLoading ||
    isPaymentTypesLoading ||
    isCurrenciesLoading ||
    isDeliveryNoteLoading;

  const isError =
    isSuppliersError ||
    hasBranchListError ||
    isWarehousesError ||
    isPaymentTypesError ||
    isCurrenciesError ||
    isDeliveryNoteError;

  const isReceiveNoteCompleted = props.receiveNote?.state === 'COMPLETED';

  const paymentTypeByReceiveNote = paymentTypes?.find((paymentType) =>
    equals(paymentType.key, props.receiveNote?.paymentType)
  );

  const currencyByWarehouse = currencies?.find((currency) =>
    equals(currency.code, props.warehouse?.currency)
  );

  const authorizationProfileByReceiveNote = authorizationProfiles?.find((authorizationProfile) =>
    equals(authorizationProfile.id, props.receiveNote?.authorizationProfileId)
  );

  const supplierByReceiveNote = getSupplierById(suppliers ?? [], props.receiveNote?.supplierId);

  const warehouseByReceiveNote = getWarehouseById(warehouses, props.receiveNote?.warehouseId);

  const sourceBranchByDeliveryNote = branchList?.branchListItems?.find((branch) =>
    equals(branch.id, props.receiveNote?.transferSourceBranchId)
  );

  const branchByDeliveryNote = branchList?.branchListItems?.find((branch) =>
    equals(branch.id, props.receiveNote?.branchId)
  );

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const deliveryNoteAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: isNotNil(deliveryNote?.deliveryNoteNumber) ? (
        <Link
          title={deliveryNote?.deliveryNoteNumber}
          rightIcon="action/open_in_new"
          size="small"
          onClick={() =>
            window.open(
              composePath(warehouseRoutes.deliveryNoteDetailOverview, {
                params: {id: deliveryNote?.deliveryNoteId},
              }),
              '_blank'
            )
          }
          data-testid={suffixTestId('deliveryNoteLink', props)}
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.warehouse'),
      value: props.warehouse?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.supplier'),
      value: supplierByReceiveNote?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.deliveryType'),
      value: match(props.receiveNote?.type)
        .with('OPENING_BALANCE', () => i18n.t('entity.warehouse.labels.openingBalance'))
        .with('PURCHASE', () => i18n.t('entity.warehouse.labels.purchase'))
        .with('TRANSFER', () => i18n.t('entity.warehouse.labels.transfer'))
        .with('EXTERNAL_TRANSFER', () => i18n.t('entity.warehouse.labels.externalTransfer'))
        .otherwise(() => EMPTY_PLACEHOLDER),
    },
    {
      label: i18n.t('entity.warehouse.labels.issuedDate'),
      value: formatToShortDate(deliveryNote?.issuedAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.createdDate'),
      value: formatToShortDate(props.receiveNote?.deliveryNote?.createdAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.completedAt'),
      value: formatToShortDate(props.receiveNote?.deliveryNote?.completedAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.createdFromReceiveNote'),
      content: (
        <Flag
          isSubtle
          colorScheme="blue"
          label={
            props.isFromReceiveNote ? i18n.t('general.labels.yes') : i18n.t('general.labels.no')
          }
          size="small"
          data-testid={suffixTestId('isFromReceiveNoteFlag', props)}
        />
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.note'),
      value: props.receiveNote?.deliveryNote?.note,
    },
  ];

  const basicInformationCardActions = buildArray<Action>().when(
    isNotNilOrEmpty(props.receiveNoteId) && isReadOnly,
    {
      type: 'button',
      variant: 'link',
      leftIcon: 'image/edit',
      title: i18n.t('general.labels.edit'),
      isDisabled: not(canEditReceiveNote),
      onClick: () => setIsReadOnly(false),
    }
  );

  const renderBasicInformation = (isReadOnly: boolean, isBranchBased: boolean) =>
    match({isReadOnly, isBranchBased})
      .with({isReadOnly: true, isBranchBased: true}, () => (
        <BranchBasedInformationReadOnly
          receiveNote={props.receiveNote}
          sourceBranch={sourceBranchByDeliveryNote}
          branch={branchByDeliveryNote}
          authorizationProfile={authorizationProfileByReceiveNote}
          warehouse={warehouseByReceiveNote}
          paymentType={paymentTypeByReceiveNote}
          data-testid={suffixTestId('forms.branchBasedInformationReadOnly', props)}
        />
      ))
      .with({isReadOnly: true, isBranchBased: false}, () => (
        <BasicInformationReadOnly
          readOnlyFields={{
            supplier: supplierByReceiveNote?.name,
            warehouse: props.warehouse?.name,
            authorizationProfile: authorizationProfileByReceiveNote?.name,
            number: props.receiveNote?.receiveNoteNumber,
            date:
              isReceiveNoteCompleted && props.receiveNote?.updatedAt
                ? props.receiveNote.updatedAt
                : null,
            invoiceNumber: props.receiveNote?.invoiceNumber,
            invoiceIssueDate: props.receiveNote?.invoiceIssueDate,
            paymentType: paymentTypeByReceiveNote?.value,
            currency: currencyByWarehouse?.code,
            exchangeRate: props.receiveNote?.exchangeRate,
            ncConversion: props.receiveNote?.ncConversion,
            deliveryNoteNumber: props.receiveNote?.deliveryNote?.deliveryNoteNumber,
            issuedAt: props.receiveNote?.deliveryNote?.deliveryNoteIssuedAt,
            note: props.receiveNote?.note,
          }}
        />
      ))
      .with({isReadOnly: false, isBranchBased: true}, () => (
        <BranchBasedInformationForm
          isCompleted={isReceiveNoteCompleted}
          receiveNote={props.receiveNote}
          branchList={branchList}
          warehouses={warehouses}
          authorizationProfiles={authorizationProfiles}
          paymentTypes={paymentTypes}
          onClose={() => setIsReadOnly(true)}
          data-testid={suffixTestId('forms.branchBasedInformation', props)}
        />
      ))
      .with({isReadOnly: false, isBranchBased: false}, () => (
        <BasicInformationForm
          isCompleted={isReceiveNoteCompleted}
          isDraft={props.isDraftReceiveNote}
          receiveNote={props.receiveNote}
          suppliers={suppliers}
          warehouses={warehouses}
          authorizationProfiles={authorizationProfiles}
          paymentTypes={paymentTypes}
          currencies={currencies}
          currencyCodeByWarehouse={currencyByWarehouse?.code}
          onClose={() => setIsReadOnly(true)}
          data-testid={suffixTestId('forms.basicInformation', props)}
        />
      ))
      .exhaustive();

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
      <Box>
        <VStack spacing={4}>
          <Card
            title={i18n.t('general.labels.basicInformation')}
            actions={basicInformationCardActions}
            data-testid={suffixTestId('cards.basicInformation', props)}
          >
            {renderBasicInformation(isReadOnly, isBranchBased)}
          </Card>
          <Card
            title={i18n.t('entity.warehouse.labels.deliveryNote')}
            data-testid={suffixTestId('cards.deliveryNote', props)}
          >
            <DataStatus isEmpty={props.isDraftReceiveNote} minHeight={60}>
              <Card variant="inlineGrey">
                <Attributes
                  rows={deliveryNoteAttributes}
                  size="quarter"
                  data-testid={suffixTestId('deliveryNoteAttributes', props)}
                />
              </Card>
            </DataStatus>
          </Card>
        </VStack>
      </Box>
    </DataStatus>
  );
};
